import './PeoplePicker.scss';
import { autobind, BaseComponent } from 'office-ui-fabric-react/lib/Utilities';
import { IPerson, IPeoplePickerQueryParams, EntityType } from '@ms/odsp-datasources/lib/PeoplePicker';
import { IPickerItemProps } from 'office-ui-fabric-react/lib/Pickers';
import { MessageBar } from 'office-ui-fabric-react/lib/MessageBar';
import { PeoplePicker as SharedPeoplePicker, SelectedItemDefault, IPeoplePicker } from '@ms/odsp-shared-react/lib/PeoplePicker';
import {
  SharingLinkKind,
  PrincipalType,
  ISharingInformation,
  AccessStatus,
  ClientId
} from '../../interfaces/SharingInterfaces';
import PlatformDetection from '@ms/odsp-utilities/lib/browser/PlatformDetection';
import { IBaseProps } from 'office-ui-fabric-react/lib/Utilities';
import * as React from 'react';
import * as PropTypes from 'prop-types';
import stringsResx from '../Share.resx';
import * as StringHelper from '@ms/odsp-utilities/lib/string/StringHelper';
import * as ClientIdHelper from '../../utilities/ClientIdHelper';
import * as ShareCommunicationManager from '../../utilities/ShareCommunicationManager';
import { ICalloutProps } from 'office-ui-fabric-react/lib/Callout';
import {
  Spinner
} from 'office-ui-fabric-react/lib/Spinner';
import Guid from '@ms/odsp-utilities/lib/guid/Guid';

export const PeoplePickerGraph = { ODB: 387 };

export interface IPeoplePickerProps extends IBaseProps {
  defaultSelectedItems: IPerson[];
  error?: JSX.Element;
  onChange: (items: any[]) => void;
  onResolvedSuggestions: (suggestions: IPerson[]) => IPerson[];
  oversharingExternalsWarning?: string;
  oversharingGroupsWarning?: string;
  pickerSettings: any;
  sharingLinkKind?: SharingLinkKind;
  sharingInformation: ISharingInformation;
  permissionsMap?: { [index: string]: AccessStatus };
  clientId: ClientId;
  disableGraph?: boolean;
  allowSPGroups?: boolean;
  inputTextPlaceholder?: string;
}

export interface IPeoplePickerState {
  selectedItems: IPerson[];
  searchForMoreText: string;
  addedUsers?: string[];
}

const PICKER_LIMIT = 100;

export default class PeoplePicker extends BaseComponent<IPeoplePickerProps, IPeoplePickerState> {
  private _peoplePickerProvider: any;
  private _peoplePicker: HTMLElement;
  private _peoplePickerControl: IPeoplePicker;
  private _platformDetection: PlatformDetection;
  private _resize: () => void;

  static contextTypes = {
    peoplePickerProvider: PropTypes.object.isRequired,
    resize: PropTypes.func.isRequired,
    platformDetection: PropTypes.object.isRequired
  };

  constructor(props: IPeoplePickerProps, context: any) {
    super(props);

    this._peoplePickerProvider = context.peoplePickerProvider;
    this._resize = context.resize;
    this._platformDetection = context.platformDetection;

    this.state = {
      selectedItems: props.defaultSelectedItems,
      searchForMoreText: this._getSearchForMoreText(props.sharingInformation.isGraphAvailable),
      addedUsers: []
    };
  }

  public componentWillReceiveProps(nextProps: IPeoplePickerProps) {
    const selectedItems = nextProps.defaultSelectedItems;
    const currentSelectedItems: IPerson[] = this.props.defaultSelectedItems;
    let addedUsers: string[] = [];
    currentSelectedItems.forEach((person) => {
      const selectedPerson = selectedItems.filter((item) => {
        return item.name === person.name;
      });
      if (selectedPerson.length > 0) {
        addedUsers.push(selectedPerson[0].name);
      }
    });

    this.setState({
      selectedItems,
      addedUsers
    });
  }

  public componentDidUpdate(prevProps: IPeoplePickerProps) {
    this._peoplePicker.scrollTop = this._peoplePicker.scrollHeight;

    // Only resize component if there's an error to display.
    if (this.props.error !== null && prevProps.error === null) {
      this._resize();
    }
  }

  public componentDidMount() {
    const isIE = this._platformDetection.isIE;
    if (this._peoplePickerControl && !ClientIdHelper.isMobile(this.props.clientId) && !isIE) {
      this._peoplePickerControl.focusInput();
    } else if (this._peoplePickerControl && isIE) {
      // this is to resolve Korean IME focus event issue.
      setTimeout(() => {
        this._peoplePickerControl.focusInput();
      }, 500);
    }
  }

  public render() {
    const inputProps: any = {
      placeholder: this._computePlaceholderText(),
      onFocus: (ev: React.FocusEvent<HTMLInputElement>) => { this._onFocus(); },
      'aria-label': this._computeAddAnotherAriaLabel()
    };

    // IPeoplePickerQueryParams
    const pickerSettings = this.props.pickerSettings;
    const peoplePickerQueryParams: IPeoplePickerQueryParams = {
      principalSource: pickerSettings.PrincipalSource,
      principalType: this._convertPrincipalType(pickerSettings.PrincipalAccountType),
      querySettings: pickerSettings.QuerySettings,
      allowEmailAddresses: true,
      filterExternalUsers: false, // Property used to filter cached external user results.
      maximumEntitySuggestions: 30,
      allowSharePointGroups: !!this.props.allowSPGroups,
      forceResolve: true,
      useGraph: !this.props.disableGraph,
      useSubstrate: true,
      onToggleGetMoreResults: this._onToggleGetMoreResults,
      conversationId: Guid.generate()
    };

    const classes = this.props.error ? 'od-Share-PeoplePicker ms-Share-PeoplePicker--error' : 'od-Share-PeoplePicker';
    const isMobile = ClientIdHelper.isMobile(this.props.clientId) || this._platformDetection.isMobile;
    const pickerCalloutProps: ICalloutProps = { preventDismissOnScroll: isMobile, preventDismissOnResize: true };
    const suggestionsAvailableAlertText = isMobile ?
      stringsResx.fabricPickerSuggestionsAvailableMobile :
      stringsResx.fabricPickerSuggestionsAvailable;
    return (
      <div
        className='od-Share-PeoplePicker-wrapper'
        role='presentation'
        ref={ (peoplePicker) => { this._peoplePicker = peoplePicker; } }>
        <span role='alert' aria-live='assertive' className='od-Share-screenReaderOnly'>{ this._computePeoplePickerLabel() }</span>
        <SharedPeoplePicker
          componentRef={ this._resolveRef('_peoplePickerControl') }
          className={ classes }
          dataProvider={ this._peoplePickerProvider }
          selectedItems={ this.state.selectedItems }
          inputProps={ inputProps }
          loadingText={ stringsResx.loadingLabel }
          noResultsFoundText={ stringsResx.noResultsLabel }
          onSelectedPersonasChange={ this.props.onChange }
          peoplePickerQueryParams={ peoplePickerQueryParams }
          suggestionsClassName={ `od-Share-PeoplePicker-Suggestions${this._peoplePickerProvider.sharePointSearch ? ' od-Share-PeoplePicker-Suggestions-condensed' : ''}` }
          onRenderItem={ this._onRenderItem }
          onSuggestionSelected={ this._onSuggestionSelected }
          onResolvedSuggestions={ this.props.onResolvedSuggestions }
          itemLimit={ PICKER_LIMIT }
          onInputChange={ this._onInputChange }
          onGetMoreResults={ this._peoplePickerProvider.sharePointSearch ? this._onGetMoreResults : null }
          searchForMoreText={ this._peoplePickerProvider.sharePointSearch ? this.state.searchForMoreText : null }
          suggestionsAvailableAlertText={ suggestionsAvailableAlertText }
          enableSelectedSuggestionAlert={ this._platformDetection.isIE }
          role='presentation'
          pickerCalloutProps={ pickerCalloutProps }
        />
        { this._renderError() }
        { this._renderWarnings() }

        <span role='alert' className='od-Share-screenReaderOnly'>{ this.props.error }</span>
      </div>
    );
  }

  private _onItemsChange = (items: IPerson[]) => {
    const newSelectedItems = this.state.selectedItems.concat(items);
    this.setState({
      selectedItems: newSelectedItems
    }, () => {
      this.props.onChange(newSelectedItems);
    });
    items.forEach(item => {
      this._onAutoResolve(item);
    });
  }

  @autobind
  private _createGenericPerson(input: string): IPerson {
    return {
      userId: input,
      name: input,
      email: input,
      isResolving: true
    };
  }

  @autobind
  private _onInputChange(input: string): string {
    // Suuport for copy/paste from OWA and outlook
    // if we encounter a semicolon, we iterate through items and
    // create generic personas that need to be resolved.
    if (input && input.indexOf(';') > -1) {
      const newPersons = input.split(';');
      const genericPersons = newPersons.map<IPerson>(newPerson => {
        const outlookRegEx = /<[^\]]*>/g;
        const outlookRegExResult = outlookRegEx.exec(newPerson);

        if (outlookRegExResult && outlookRegExResult[0]) {
          newPerson = outlookRegExResult[0].substring(1, outlookRegExResult[0].length - 1);
        }
        newPerson = newPerson.trim();
        if (newPerson) {
          return this._createGenericPerson(newPerson);
        }
      })
        .filter(genericPerson => genericPerson);
      this._onItemsChange(genericPersons);
      // When the ; is input we want the picker to clear out and be treated as though the input returned
      // an empty string
      return '';
    }

    // This rule sanitizes input that is surrounded by single or double quotes.
    const quoteRegEx = /['"].*['"]/g;
    const quoteRegExResult = quoteRegEx.exec(input);
    if (quoteRegExResult && quoteRegExResult[0]) {
      return quoteRegExResult[0].substring(1, quoteRegExResult[0].length - 1);
    }

    return input;
  }

  @autobind
  private _onGetMoreResults(query: string): IPerson[] | PromiseLike<IPerson[]> {
    // IPeoplePickerQueryParams
    const pickerSettings = this.props.pickerSettings;
    const peoplePickerQueryParams: any = {
      principalSource: pickerSettings.PrincipalSource,
      principalType: this._convertPrincipalType(pickerSettings.PrincipalAccountType),
      querySettings: pickerSettings.QuerySettings,
      allowEmailAddresses: true,
      filterExternalUsers: false, // Property used to filter cached external user results.
      maximumEntitySuggestions: 30,
      allowSharePointGroups: false,
      forceResolve: true
    };
    return this._peoplePickerProvider.sharePointSearch(query, peoplePickerQueryParams);
  }

  @autobind
  private _onSuggestionSelected(suggestion: IPerson): IPerson | PromiseLike<IPerson> {
    const pickerSettings = this.props.pickerSettings;
    const peoplePickerQueryParams: any = {
      principalSource: pickerSettings.PrincipalSource,
      principalType: this._convertPrincipalType(pickerSettings.PrincipalAccountType),
      querySettings: pickerSettings.QuerySettings,
      allowEmailAddresses: pickerSettings.AllowEmailAddresses,
      filterExternalUsers: false, // Property used to filter cached external user results.
      maximumEntitySuggestions: 30,
      allowSharePointGroups: false,
      forceResolve: true
    };

    // If suggestion is an internal user, skip resolve.
    if (suggestion.isExternal === false || (suggestion.isExternal && suggestion.providerName)) {
      return suggestion;
    } else if (suggestion.principalType === PrincipalType.distributionList) {
      peoplePickerQueryParams.isGraphDistributionList = true;
      peoplePickerQueryParams.useGraph = true;
    }

    return this._peoplePickerProvider.resolve(suggestion.userId, peoplePickerQueryParams).then((person: IPerson) => {
      /**
       * Workaround for case when it's an existing authenticated user
       * in a tenant and API doesn't return an email in the email property.
       */
      if (!person.email && person.rawData && person.rawData.EntityData) {
        person.email = person.rawData.EntityData.OtherMails;
      }

      if (!person.name && person.rawData && person.rawData.EntityData) {
        person.name = person.rawData.EntityData.OtherMails;
      }

      // Used for telemetry only
      if (suggestion.entityType === EntityType.linkedinUser) {
        person.entityType = suggestion.entityType;
      }

      return person;
    });
  }

  @autobind
  private _onAutoResolve(input: IPerson): void {
    const pickerSettings = this.props.pickerSettings;
    const peoplePickerQueryParams: any = {
      principalSource: pickerSettings.PrincipalSource,
      principalType: this._convertPrincipalType(pickerSettings.PrincipalAccountType),
      querySettings: pickerSettings.QuerySettings,
      allowEmailAddresses: pickerSettings.AllowEmailAddresses,
      filterExternalUsers: false, // Property used to filter cached external user results.
      maximumEntitySuggestions: 30,
      allowSharePointGroups: false,
      forceResolve: true
    };
    this._peoplePickerProvider.resolve(input.email, peoplePickerQueryParams).then((person: IPerson) => {
      /**
       * Workaround for case when it's an existing authenticated user
       * in a tenant and API doesn't return an email in the email property.
       */
      if (!person.email && person.rawData && person.rawData.EntityData) {
        person.email = person.rawData.EntityData.OtherMails;
      }

      if (!person.name && person.rawData && person.rawData.EntityData) {
        person.name = person.rawData.EntityData.OtherMails;
      }

      // change the unresolved temporary genericPersona with the resolved one
      const newSelectedItems: IPerson[] = [];
      this.state.selectedItems.forEach(selectedItem => {
        if (selectedItem.email === input.email) {
          newSelectedItems.push(person);
        } else {
          newSelectedItems.push(selectedItem);
        }
      });

      this.setState({
        selectedItems: newSelectedItems
      }, () => {
        this.props.onChange(newSelectedItems);
      });
    }).catch(() => {
      /**
       * Mark the failed resolve at no longer resolving
       * which should render an error for the respective item.
       */
      const person: IPerson = { ...input, isResolving: false };

      // change the unresolved temporary genericPersona with the resolved one
      const newSelectedItems: IPerson[] = [];
      this.state.selectedItems.forEach(selectedItem => {
        if (selectedItem.email === input.email) {
          newSelectedItems.push(person);
        } else {
          newSelectedItems.push(selectedItem);
        }
      });
      this.setState({
        selectedItems: newSelectedItems
      }, () => {
        this.props.onChange(newSelectedItems);
      });
    });
  }

  @autobind
  private _isResolveInProgress(): boolean {
    for (let selectedItem of this.state.selectedItems) {
      if (selectedItem.isResolving) {
        return true;
      }
    }
    return false;
  }

  @autobind
  private _onRenderItem(props: IPickerItemProps<IPerson>): JSX.Element {
    const permissionsMap = this.props.permissionsMap;

    /**
     * Checks 2 cases:
     *  - If external users are not allowed (policy or link type).
     *  - If direct link is being sent an user doesn't have permission to the item.
     *  - If user is unresolved (i.e. no match found).
     */
    const allowedExternalUsers = this._computeAllowExternalUsers(this.props.sharingLinkKind, this.props.sharingInformation.canAddExternalPrincipal);
    const isError =
      (props.item.isExternal && !allowedExternalUsers) ||
      (this.props.sharingLinkKind === SharingLinkKind.direct && permissionsMap &&
        (permissionsMap[props.item.email] !== undefined && permissionsMap[props.item.email] === AccessStatus.none ||
          permissionsMap[props.item.userId] !== undefined && permissionsMap[props.item.userId] === AccessStatus.none)) ||
      !props.item.isResolved && !props.item.isResolving;

    // Add aria-labels.
    const itemName = props.item.name;
    props.removeButtonAriaLabel = StringHelper.format(stringsResx.removePersonLabel, itemName);
    props.item.imageAlt = StringHelper.format(stringsResx.userPhotoAriaLabel, itemName);

    if (isError) {
      return (
        <div className='od-Share-PeoplePicker-selectedItem--error'>
          <SelectedItemDefault { ...props } />
        </div>
      );
    } else {
      return props.item.isResolving ? <Spinner className='od-Share-PeoplePicker-resolve-spinner' /> : <SelectedItemDefault { ...props } />;
    }
  }

  private _computePlaceholderText(): string {
    if (this.props.inputTextPlaceholder && this.props.inputTextPlaceholder.length > 0) {
      return this.props.inputTextPlaceholder;
    }

    if (this.props.defaultSelectedItems.length === 0) {
      return stringsResx.peoplePickerPlaceholder;
    } else {
      return stringsResx.peoplePickerPlaceholderWithSelection;
    }
  }

  // Returns the screen reader only string for when a person is added / removed.
  private _computePeoplePickerLabel(): string {
    const { addedUsers } = this.state;
    let labels = [];
    if (addedUsers.length > 0) {
      labels = labels.concat(addedUsers.map((person) => {
        return StringHelper.format(stringsResx.peoplePickerPersonAddedAlert, person);
      }));
    }
    return labels.length > 0 ? labels.toString() : '';
  }

  // Converts comma-separated account selection text to SPPrincipalType.
  // TODO (joem): Convert to use enums/PrincipalType if this stays.
  private _convertPrincipalType(principalTypes: string): number {
    if (!principalTypes) {
      return PrincipalType.none;
    }

    let result: PrincipalType = PrincipalType.none;
    const types: Array<string> = principalTypes.split(',');
    for (const type of types) {
      if (type === 'User') {
        result |= PrincipalType.user;
      }
      if (type === 'DL') {
        result |= PrincipalType.distributionList;
      }
      if (type === 'SecGroup') {
        result |= PrincipalType.securityGroup;
      }
      if (type === 'SPGroup') {
        result |= PrincipalType.sharePointGroup;
      }
    }

    return result;
  }

  private _computeAllowExternalUsers(sharingLinkKind: number, canAddExternalPrincipal: boolean): boolean {
    const canOrganizationView = sharingLinkKind === SharingLinkKind.organizationView;
    const canOrganizationEdit = sharingLinkKind === SharingLinkKind.organizationEdit;
    const isWithinOrganization = (canOrganizationView || canOrganizationEdit);
    return !isWithinOrganization && canAddExternalPrincipal;
  }

  private _renderError() {
    const error = this.props.error;

    if (error && !this._isResolveInProgress()) {
      /**
       * Gives focus back to PeoplePicker input if input needs to be addressed.
       */
      if (this._peoplePickerControl && !ClientIdHelper.isMobile(this.props.clientId)) {
        this._peoplePickerControl.focusInput();
      }

      return (
        <span
          className='od-Share-PeoplePicker-error'
          role='alert'
        >
          { error }
        </span>
      );
    }
  }

  private _renderWarnings() {
    const oversharingExternalsWarning = this.props.oversharingExternalsWarning;
    const oversharingGroupsWarning = this.props.oversharingGroupsWarning;
    const warnings = [];

    // Don't show warnings if there is an error to resolve.
    if (this.props.error) {
      return;
    }

    if (this.state.selectedItems.length === PICKER_LIMIT) {
      warnings.push(
        <MessageBar ariaLabel={ stringsResx.peoplePickerLimitMessage }>{ stringsResx.peoplePickerLimitMessage }</MessageBar>
      );
    }

    if (oversharingExternalsWarning) {
      warnings.push(
        <MessageBar ariaLabel={ oversharingExternalsWarning }>{ oversharingExternalsWarning }</MessageBar>
      );
    }

    if (oversharingGroupsWarning) {
      warnings.push(
        <MessageBar ariaLabel={ oversharingGroupsWarning }>{ oversharingGroupsWarning }</MessageBar>
      );
    }

    if (warnings.length > 0) {
      return (
        <div aria-live='assertive' role='status' className='od-Share-PeoplePicker-warning'>
          { warnings }
        </div>
      );
    }
  }

  @autobind
  private _getSearchForMoreText(showGetMoreResults: boolean): string {
    return showGetMoreResults ? stringsResx.searchForMoreText : undefined;
  }

  @autobind
  private _onToggleGetMoreResults(showGetMoreResults: boolean): void {
    if (this.state && ((showGetMoreResults && !this.state.searchForMoreText) || (!showGetMoreResults && this.state.searchForMoreText))) {
      this.setState({ searchForMoreText: this._getSearchForMoreText(showGetMoreResults) });
    }
  }

  @autobind
  private _onFocus() {
    ShareCommunicationManager.peoplePickerFocused();
  }

  @autobind
  private _computeAddAnotherAriaLabel(): string {
    if (this.props.defaultSelectedItems.length === 0) {
      return stringsResx.selectedItemListLabel;
    } else {
      return stringsResx.selectedItemAddAnotherAriaLabel;
    }
  }
}
